import { NavLink, Link } from "react-router-dom";
import {
  LayoutDashboard,
  ServerCog,
  Users,
  Layers,
  Activity,
  Wrench,
  Archive,
  Boxes,
  BellRing,
  ScrollText,
  Filter,
  HardDrive,
  Skull,
  Settings as SettingsIcon,
} from "lucide-react";

import { fa } from "@/locales/fa";
import { cn } from "@/lib/utils";

export type NavItem = { to: string; icon: typeof LayoutDashboard; label: string; group?: string };

// Two semantic groups so the sidebar reads as a clean list, not 12 cramped
// icons. "ops" items live below the divider. Exported so the mobile nav
// drawer (components/layout/MobileNav.tsx) renders the exact same list and
// the two navigations never drift apart.
export const navItems: NavItem[] = [
  { to: "/dashboard",     icon: LayoutDashboard, label: fa.nav.dashboard },
  { to: "/servers",       icon: ServerCog,       label: fa.nav.servers },
  { to: "/accounts",      icon: Users,           label: fa.nav.accounts },
  { to: "/groups",        icon: Layers,          label: fa.nav.groups },
  { to: "/excludes",      icon: Filter,          label: fa.nav.excludes },
  { to: "/storage",       icon: HardDrive,       label: fa.nav.storage },
  { to: "/graveyard",     icon: Skull,           label: fa.nav.graveyard },
  { to: "/jobs",          icon: Activity,        label: fa.nav.jobs, group: "ops" },
  { to: "/snapshots",     icon: Boxes,           label: fa.nav.snapshots, group: "ops" },
  { to: "/restore",       icon: Archive,         label: fa.nav.restore, group: "ops" },
  { to: "/notifications", icon: BellRing,        label: fa.nav.notifications, group: "ops" },
  { to: "/ops",           icon: Wrench,          label: fa.nav.ops, group: "ops" },
  { to: "/audit",         icon: ScrollText,      label: fa.nav.audit, group: "ops" },
  { to: "/settings",      icon: SettingsIcon,    label: fa.nav.settings, group: "ops" },
];

export function Sidebar() {
  return (
    <aside className="hidden md:flex md:w-60 shrink-0 border-l border-border bg-card/40">
      <div className="flex w-full flex-col">
        {/* Logo block — clickable, navigates to the dashboard. Operators
            instinctively click logos to "go home"; before this change the
            mark was a plain <img> + text and clicks did nothing, sending
            them to the sidebar menu instead. Whole row is the hit area
            so even the gap between mark and wordmark counts. */}
        <Link
          to="/"
          className="flex items-center gap-2.5 px-5 h-14 border-b border-border hover:bg-muted/40 transition-colors"
          aria-label={fa.appShort}
        >
          {/* White rounded plate behind the logo. White works in both themes:
              in dark-mode it gives the colored mark a high-contrast canvas; in
              light-mode the subtle ring carves it out from the page background. */}
          <div className="grid place-items-center size-8 rounded-lg bg-white ring-1 ring-border shadow-sm">
            <img
              src="/logo.webp"
              alt={fa.appShort}
              className="size-6 object-contain"
              draggable={false}
            />
          </div>
          <span className="text-sm font-semibold tracking-tight">{fa.appShort}</span>
        </Link>

        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <ul className="space-y-0.5">
            {navItems.map(({ to, icon: Icon, label, group }, idx) => {
              const prev = navItems[idx - 1];
              const showDivider = group === "ops" && prev?.group !== "ops";
              return (
                <li key={to}>
                  {showDivider && (
                    <div className="mx-2 my-3 h-px bg-border" aria-hidden />
                  )}
                  <NavLink
                    to={to}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                        isActive
                          ? "bg-muted text-foreground font-medium"
                          : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                      )
                    }
                  >
                    <Icon className="size-[16px] shrink-0" strokeWidth={1.75} />
                    <span>{label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="px-5 py-3 border-t border-border text-[11px] text-muted-foreground/70 tabular-nums">
          v0.1.0 · {new Date().toISOString().slice(0, 10)}
        </div>
      </div>
    </aside>
  );
}
